import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useState } from 'react'
import { FaCode, FaServer, FaPaintBrush, FaChalkboardTeacher, FaCheckCircle } from 'react-icons/fa'
import { SiGithub, SiPostman, SiAdobephotoshop, SiFigma } from 'react-icons/si'
import { VscVscode } from "react-icons/vsc";

const Skills = () => {
  const [activeTab, setActiveTab] = useState('expertise')
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const skillVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  }

  // Core expertise areas with detailed descriptions
  const expertiseAreas = [
    {
      icon: <FaCode />,
      title: "Frontend Development",
      description: "Creating responsive, interactive user interfaces with modern JavaScript frameworks. Specializing in React for building component-based UIs with optimal performance.",
      skills: [
        "Single Page Applications (SPAs)",
        "Responsive Web Design",
        "UI/UX Implementation",
        "State Management",
        "Component Architecture"
      ]
    },
    {
      icon: <FaServer />,
      title: "Backend Development",
      description: "Building robust server-side applications with focus on API design, authentication systems, and efficient database operations.",
      skills: [
        "RESTful API Design",
        "Authentication & Authorization",
        "Server-side Rendering",
        "Microservices Architecture",
        "Data Validation"
      ]
    },
    {
      icon: <FaPaintBrush />,
      title: "UI/UX Design Integration",
      description: "Converting design mockups into working code with attention to colors, spacing, and user experience.",
      skills: [
        "Turning Designs into Working Code",
        "Smooth Animations",
        "Making Websites Accessible",
        "Working with Design Systems",
        "Support for All Browsers"
      ]
    },
    {
      icon: <FaChalkboardTeacher />,
      title: "Technical Education",
      description: "Sharing software development expertise through clear instruction and mentorship. Converting complex technical concepts into understandable learning materials.",
      skills: [
        "Technical Instruction",
        "Developer Mentorship",
        "Knowledge Transfer",
        "Concept Simplification",
        "Educational Content Creation"
      ]
    }
  ]

  // Professional development & tools
  const professionalTools = [
    {
      icon: <VscVscode color="#007ACC" />,
      name: "VS Code",
      category: "Development"
    },
    {
      icon: <SiGithub color="##6e5494" />,
      name: "Git & GitHub",
      category: "Version Control"
    },
    {
      icon: <SiPostman color="#FF6C37" />,
      name: "Postman",
      category: "API Testing"
    },
    {
      icon: <SiAdobephotoshop color="#31A8FF" />,
      name: "Adobe Photoshop",
      category: "Design"
    },
    {
      icon: <SiFigma color="#F24E1E" />,
      name: "Figma",
      category: "Design"
    }
  ];
  

  // Certifications & education
  const certifications = [
    {
      title: "Mobile App Development with Flutter",
      issuer: "Code IT",
      date: "2021"
    },
    {
      title: " Web Development with Laravel",
      issuer: "Hunchha Digital",
      date: "2023"
    },
    {
      title: "Backend Development Internship",
      issuer: "Youth IT",
      date: "2024"
    }
  ]

  return (
    <section id="skills" className="py-20 bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 z-0"></div>
      <div className="absolute top-40 right-0 w-72 h-72 bg-blue-300 dark:bg-blue-900 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
      <div className="absolute bottom-40 left-40 w-72 h-72 bg-purple-300 dark:bg-purple-900 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-extrabold text-gray-900 dark:text-white mb-6">
            <span className="bg-clip-text font-montserrat text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              Skills & Expertise
            </span>
          </h2>
          <p className="text-xl font-mono text-gray-700 dark:text-gray-200 max-w-3xl mx-auto leading-relaxed">
            My comprehensive skill set and specialized expertise enable me to tackle complex web development 
            challenges and deliver polished, high-performance solutions.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex font-robotoMono flex-wrap justify-center gap-4 mb-12">
          {['expertise', 'tools', 'certifications'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 rounded-full text-base font-medium transition-all ${
                activeTab === tab
                  ? "bg-blue-600 text-white shadow-lg shadow-blue-500/30"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Content Container */}
        <div 
          ref={ref}
          className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 border border-gray-100 dark:border-gray-700"
        >
          {/* Expertise Areas */}
          {activeTab === 'expertise' && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              className="grid md:grid-cols-2 gap-8"
            >
              {expertiseAreas.map((area, index) => (
                <motion.div
                  key={index}
                  variants={skillVariants}
                  className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
                >
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 text-xl mr-4">
                      {area.icon}
                    </div>
                    <h3 className="text-xl font-montserrat font-bold text-gray-900 dark:text-white">{area.title}</h3>
                  </div>
                  <p className="text-gray-700 text-justify font-robotoMono dark:text-gray-200 mb-5">{area.description}</p>
                  <div className="space-y-2">
                    {area.skills.map((skill, idx) => (
                      <div key={idx} className="flex items-center">
                        <FaCheckCircle className="text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700 font-mono dark:text-gray-200">{skill}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Professional Tools */}
          {activeTab === 'tools' && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
            >
              <p className="text-gray-700 font-montserrat dark:text-gray-300 mb-8">
                Beyond programming languages and frameworks, I leverage these professional tools to streamline development workflows and enhance productivity.
              </p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {professionalTools.map((tool, index) => (
                  <motion.div
                    key={index}
                    variants={skillVariants}
                    className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 flex flex-col items-center justify-center text-center border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
                  >
                    <div className="text-4xl mb-3 text-gray-700 dark:text-gray-300">
                      {tool.icon}
                    </div>
                    <h4 className="font-medium font-robotoMono text-gray-900 dark:text-white mb-1">{tool.name}</h4>
                    <p className="text-sm text-gray-500 font-robotomono dark:text-gray-400">{tool.category}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Certifications & Education */}
          {activeTab === 'certifications' && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
            >
              <p className="text-gray-700 font-robotoMono dark:text-gray-300 mb-8">
              I actively pursue professional development through certified training programs and practical internships.
              </p>
              
              <div className="grid md:grid-cols-3 gap-2">
                {certifications.map((cert, index) => (
                  <motion.div
                    key={index}
                    variants={skillVariants}
                    className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 p-6 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
                  >
                    <div className="mb-2 text-blue-600 dark:text-blue-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <h4 className="text-xl  font-bold font-montserrat text-gray-900 dark:text-white mb-2">{cert.title}</h4>
                    <p className="text-blue-600 font-mono font-semibold dark:text-blue-400">{cert.issuer}</p>
                    <p className="text-sm text-gray-500 font-mono dark:text-gray-300 mt-1">{cert.date}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-1/4 left-0 w-24 h-24 bg-primary/5 rounded-full blur-xl"></div>
        <div className="absolute bottom-1/4 right-0 w-32 h-32 bg-secondary/5 rounded-full blur-xl"></div>
      </div>
    </section>
  )
}

export default Skills